import DeleteIcon from '@mui/icons-material/Delete';
import {
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Persona } from '../../constants/Personas';
import { FirebaseApi } from '../../firebase/firebaseApi';
import { sortByField } from '../helpers/sortHelpers';
import { LoadingDots } from '../Management/subcomponents/LoadingDots';

export type AdminPersonasProps = {
	userID: string;
	firebaseApi: Pick<FirebaseApi, 'personasByUserID' | 'deletePersona'>;
	handlePersonaOnClick: (persona: Persona) => void;
};

export const AdminPersonas = ({
	firebaseApi,
	userID,
	handlePersonaOnClick,
}: AdminPersonasProps): JSX.Element => {
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [personas, setPersonas] = useState<Persona[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const filteredPersonas = personas.filter((persona) => {
		const searchedFields = [
			persona.name.toLowerCase(),
			persona.company.name.toLowerCase(),
			persona.site.name.toLowerCase(),
			persona.accountType.toLowerCase(),
		];
		return searchedFields.some((field) =>
			field.includes(searchTerm.toLowerCase()),
		);
	});

	useEffect(() => {
		setLoading(true);
		return firebaseApi.personasByUserID(userID, (personas) => {
			const sortedPersonas = sortByField(personas, 'name');
			setPersonas(sortedPersonas);
			setLoading(false);
		});
	}, [firebaseApi, userID]);

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearchTerm(event.target.value.trim());
	};

	const renderPersona = (persona: Persona): JSX.Element => {
		const company =
			persona.company.name === '' ? 'No Company' : persona.company.name;
		const site = persona.site.name === '' ? 'No Site' : persona.site.name;
		const contractedTo = persona.contractedTo?.name ?? 'Not Contracted';
		const helperText = `${persona.accountType} - ${company} - ${site} - ${contractedTo}`;
		return (
			<ListItem
				disablePadding
				sx={{
					border: `1px solid ${theme.palette.border.dark}`,
					borderRadius: '4px',
					mb: 2,
				}}
				key={persona.id}
				secondaryAction={
					<IconButton
						edge="end"
						color="primary"
						onClick={async (): Promise<void> =>
							await firebaseApi.deletePersona(userID, persona.id)
						}>
						<DeleteIcon />
					</IconButton>
				}>
				<ListItemButton
					onClick={(): void => handlePersonaOnClick(persona)}>
					<ListItemText
						primary={
							<Typography variant="subtitle1">
								{persona.name}
							</Typography>
						}
						secondary={
							<Typography variant="caption">
								{helperText}
							</Typography>
						}
					/>
				</ListItemButton>
			</ListItem>
		);
	};

	return (
		<Stack direction="column" width="100%" sx={{ maxHeight: '50vh' }}>
			<List
				dense
				sx={{ mb: 'auto', width: '100%', flexShrink: 0, paddingX: 2 }}>
				<ListItem
					disablePadding
					sx={{
						borderRadius: '4px',
					}}>
					<TextField
						fullWidth
						placeholder="Search Personas"
						onChange={handleSearch}
					/>
				</ListItem>
			</List>
			<List
				dense
				sx={{
					width: '100%',
					padding: 2,
					overflow: 'auto',
					flexGrow: 1,
				}}>
				{loading ? (
					<LoadingDots />
				) : (
					filteredPersonas.map((persona) => renderPersona(persona))
				)}
			</List>
		</Stack>
	);
};
